import angular from 'angular';

import { getLastEditedByName } from '@web/timeClock/utils/getLastEditedByName';

import ChooseApplicantCtrl from '../../shifts/ChooseApplicantCtrl';
import ClockInCtrl from '../components/ClockInCtrl';
import modalChooseApplicantUrl from '../../shifts/modal-choose-applicant.html';
import modalClockInUrl from '../components/modal-clock-in.html';
import { SCHEDULING_CONFLICTS_ABORT } from '../../constants';
import { NOTIFICATION_TYPES_WITH_AVATARS } from '../constants';
import { ICONS_MAP } from '../../components/constants';
import config from '../../config';

export default function Notifications(
  Authentication,
  Restangular,
  ShiftUtils,
  NotificationCollection,
  AlertService,
  PersonaCollection,
  ConversationService,
  extendCalendarModel,
  Utils,
  $q,
  $uibModal
) {
  'ngInject';

  function handleSlingInternalLink(notification, action) {
    const route = action.url.slice('sling://'.length);
    if (/^shifts\/[^/]+\/choose-applicant$/.test(route)) {
      $uibModal
        .open({
          templateUrl: modalChooseApplicantUrl,
          controller: ChooseApplicantCtrl,
          controllerAs: 'ctrl',
          size: '620',
          resolve: {
            shift() {
              return notification.items[0];
            },
            isDeny() {
              return false;
            },
          },
        })
        .result.ignoreCancel();

      return null;
    } else if (/^conversations\/start(\/[^/]+)?$/.test(route)) {
      const [, , userId] = route.match(/^conversations\/start(\/([^/]+))?$/);
      ConversationService.openConversation(userId);
      return $q(() => {}); // neverending promise to keep the button in progress until navigated away
    } else if (route === 'shifts/view/clockin') {
      $uibModal
        .open({
          templateUrl: modalClockInUrl,
          controller: ClockInCtrl,
          controllerAs: 'ctrl',
          size: '620',
          resolve: {
            shifts: () => notification.items,
            readOnly: () => false,
            lastLocationAndPosition: () => ({}),
            upcomingShifts: () => [],
          },
        })
        .result.ignoreCancel();

      return null;
    }
  }

  async function performNotificationAsyncAction(notification, action) {
    await ShiftUtils.loadAsync();
    if (action.type === 'affirmative' && notification.type === 'shift_application_submitted') {
      const [shift] = notification.items;
      await ShiftUtils.approveApplications(shift, [shift.applications[0].id]);
      return;
    }

    let ignoreSuffix;
    if (notification.type === 'shift_swap_accepted') {
      ignoreSuffix = 'approve this exchange anyway';
    }

    if (notification.type === 'leave_submitted') {
      ignoreSuffix = 'approve this time off request anyway';
    }

    let checkRestBreakConflicts = false;
    if (config.CLOPENING_CONFLICT_ENABLED) {
      checkRestBreakConflicts = true;
    }

    const checkConsecutiveWorkDaysConflicts = config.CONSECUTIVE_WORK_DAYS_LIMIT_ENABLED;

    try {
      const data = await ShiftUtils.performConflictingRequest({
        request: (ignoreConflicts) =>
          Restangular.all(action.url)
            .withHttpConfig({ ignoreGlobalErrorHandler: true })
            .post(null, {
              ignoreConflicts,
              checkLeaveConflicts: 'all',
              checkRestBreakConflicts,
              checkConsecutiveWorkDaysConflicts,
            }),
        onlyManagerCanIgnore: false,
        ignoreSuffix,
        canResolveConflicts: ['leave_submitted'].includes(notification.type),
      });
      NotificationCollection.addOrEditNotification(data);

      handleSelfInviteNotificationAction(notification, action, data);
    } catch (error) {
      if (error && error !== SCHEDULING_CONFLICTS_ABORT) {
        AlertService.add(Utils.getErrorMessages(error));
      }

      throw error;
    }
  }

  function handleSelfInviteNotificationAction(notification, action, data) {
    const [user] = data?.items || [];
    if (action.type === 'affirmative' && notification.type === 'user_self_invited' && user) {
      PersonaCollection.addUser(user);
    }
  }

  async function actOnClick(action) {
    const notification = this;
    if (/^sling:\/\//.test(action.url)) {
      return handleSlingInternalLink(notification, action);
    }

    if (/^https?:\/\//.test(action.url)) {
      await performNotificationAsyncAction(
        notification,
        notification.actions.find((item) => item.type === 'neutral')
      );
      return (location.href = action.url);
    }

    return performNotificationAsyncAction(notification, action);
  }

  function restangularizeBasedOnCatetory(notification) {
    switch (notification.category) {
      case 'announcement':
        Restangular.restangularizeCollection(null, notification.items, 'announcements');
        break;

      case 'shift':
      case 'leave':
      case 'availability':
      case 'clock':
      case 'timesheet':
        notification.items = notification.items.map((shift) => {
          const extended = extendCalendarModel(shift);

          if (notification.category === 'timesheet') {
            extended.lastEditedByName = getLastEditedByName(shift, PersonaCollection.personasDict);
          }

          return extended;
        });
        break;

      case 'task':
        Restangular.restangularizeCollection(null, notification.items, 'tasks');
        break;

      default: // eslint requirement
        break;
    }
  }

  function handleLateClockInNotification(notification) {
    if (notification.type === 'clock_in_missing') {
      notification.iconUrl = ICONS_MAP['time-red'];
      notification.color = 'red';

      if (!Authentication.user.orgSettings.clockInFromWebAndApps) {
        const removeIndex = notification.actions.findIndex((a) => a.type === 'affirmative');
        if (removeIndex > -1) {
          notification.actions.splice(removeIndex, 1);
        }
      } else {
        const affirmativeAction = notification.actions.find((a) => a.type === 'affirmative');
        if (affirmativeAction) {
          affirmativeAction.stateless = true;
        }
      }
    }
  }

  Restangular.extendModel('notifications', (notification) => {
    try {
      if (!notification.items) {
        notification.items = [];
      }

      notification.isSummary = notification.items.some((item) => item.summary);

      if (notification.actor) {
        if (angular.isArray(notification.actor)) {
          notification.actor = notification.actor[0];
        }

        notification.actor = Restangular.restangularizeElement(null, notification.actor, 'users');
      }

      notification.showActorAvatar = NOTIFICATION_TYPES_WITH_AVATARS.includes(notification.type);

      const categoryToDefaultIcon = {
        announcement: 'announcement-blue',
        shift: 'shift-blue',
        leave: 'shift-leave-blue',
        task: 'task-blue',
        availability: 'time-blue',
        subscription: 'dashboard-blue',
        timesheet: 'time-blue',
        exchange: 'shift-swap-blue',
      };

      if (!notification.icon) {
        // this is being set and returned by the server at marshal time (not in DB)
        notification.icon = categoryToDefaultIcon[notification.category];
      }

      notification.color = notification.icon ? notification.icon.split('-').slice(-1)[0] : 'blue'; // blue/green/yellow/red/gray

      notification.iconUrl = ICONS_MAP[notification.icon];
      if (!('iconUrl' in notification)) {
        console.log(`unsuppored icon: ${notification.icon}`);
        notification.icon = categoryToDefaultIcon[notification.category];

        if (notification.icon) {
          notification.iconUrl = ICONS_MAP[notification.icon];
        } else {
          notification.iconUrl = ICONS_MAP['shift-blue'];
        }
      }

      notification.actOnClick = actOnClick;
      restangularizeBasedOnCatetory(notification);

      handleLateClockInNotification(notification);

      return notification;
    } catch (e) {
      console.warn('malformed notification', e);
    }
  });

  return Restangular.service('notifications');
}
